import { useMemo, useState } from 'react';
import {
  Plus, Search, ClipboardList, AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { useTickets } from '@/hooks/useProduction';
import {
  TICKET_STATUSES, TICKET_STATUS_LABELS, STATIONS, STATION_LABELS,
  type TicketStatus, type Station, type WorkTicket,
} from '@/types/production';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { TicketCard } from '@/components/production/TicketCard';
import { TicketFormModal } from '@/components/production/TicketFormModal';

export function TicketsListPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'production', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | TicketStatus>('');
  const [station, setStation] = useState<'' | Station>('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useTickets();

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((t) => {
      if (status && t.status !== status) return false;
      if (station) {
        const current = t.routing[t.currentStepIndex]?.station;
        if (current !== station) return false;
      }
      if (search.trim()) {
        const s = search.toLowerCase();
        const hay = `${t.number} ${t.description} ${t.clientName ?? ''} ${t.orderNumber ?? ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, status, station]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load tickets"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by ticket #, description, client…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as TicketStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              ...TICKET_STATUSES.map((s) => ({ value: s, label: TICKET_STATUS_LABELS[s] })),
            ]}
          />
        </div>
        <div className="w-44">
          <Select
            value={station}
            onChange={(e) => setStation(e.target.value as Station | '')}
            options={[
              { value: '', label: 'All stations' },
              ...STATIONS.map((s) => ({ value: s, label: STATION_LABELS[s] })),
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Ticket
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title={search || status || station ? 'No tickets match your filters' : 'No tickets yet'}
            description={
              search || status || station
                ? 'Try clearing some filters.'
                : 'Create tickets manually or generate them from a scheduled order.'
            }
            action={canWrite && !search && !status && !station && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>New Ticket</Button>
            )}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((t: WorkTicket) => (
            <TicketCard key={t.id} ticket={t} />
          ))}
        </div>
      )}

      <TicketFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
